import { supabase } from './supabase'

const BUCKET_NAME = 'merchant-photos'

/**
 * Get public URL for a merchant photo from Supabase storage
 * @param {Object} merchantPhoto - Merchant photo record with bucket and path
 * @returns {string|null} Public URL or null
 */
export function getMerchantPhotoUrl(merchantPhoto) {
	if (!merchantPhoto || !merchantPhoto.bucket || !merchantPhoto.path) return null

	const { data } = supabase.storage
		.from(merchantPhoto.bucket)
		.getPublicUrl(merchantPhoto.path)

	if (!data?.publicUrl) return null

	// Add cache-busting parameter using updated_at timestamp to force refresh after re-upload
	const cacheBuster = merchantPhoto.updated_at
		? `?t=${new Date(merchantPhoto.updated_at).getTime()}`
		: ''

	return data.publicUrl + cacheBuster
}

/**
 * Fetch merchant logo (is_avatar = true) from merchant_photos table
 * @param {string} merchantId - UUID of the merchant
 * @returns {Promise<Object|null>} Merchant photo record or null
 */
export async function fetchMerchantLogo(merchantId) {
	if (!merchantId) return null

	const { data, error } = await supabase
		.from('merchant_photos')
		.select('*')
		.eq('merchant_id', merchantId)
		.eq('is_avatar', true)
		.maybeSingle()

	if (error) {
		console.error('Error fetching merchant logo:', error)
	}

	return data || null
}

/**
 * Fetch all gallery photos (is_avatar = false) for a merchant
 * @param {string} merchantId - UUID of the merchant
 * @returns {Promise<Array>} Array of merchant photo records sorted by order
 */
export async function fetchMerchantGalleryPhotos(merchantId) {
	if (!merchantId) return []

	const { data, error } = await supabase
		.from('merchant_photos')
		.select('*')
		.eq('merchant_id', merchantId)
		.eq('is_avatar', false)
		.order('order', { ascending: true })

	if (error) {
		console.error('Error fetching merchant gallery photos:', error)
		return []
	}

	return data || []
}

/**
 * Get the next order number for a new gallery photo
 * @param {string} merchantId - UUID of the merchant
 * @returns {Promise<number>} Next order number (starting from 1)
 */
export async function getNextGalleryPhotoOrder(merchantId) {
	if (!merchantId) return 1

	const { data, error } = await supabase
		.from('merchant_photos')
		.select('order')
		.eq('merchant_id', merchantId)
		.eq('is_avatar', false)
		.order('order', { ascending: false })
		.limit(1)
		.maybeSingle()

	if (error) {
		console.error('Error getting next order:', error)
		return 1
	}

	return (data?.order || 0) + 1
}

/**
 * Bulk update photo orders in a single database call
 * @param {Array<{id: string, order: number}>} updates - Array of photo IDs and their new orders
 * @returns {Promise<boolean>} Success status
 */
export async function updatePhotoOrders(updates) {
	if (!updates || updates.length === 0) return true

	try {
		// Use Promise.all with individual updates wrapped in a transaction-like pattern
		// Supabase doesn't support true transactions in JS client, but we can do batch updates
		const { error } = await supabase.rpc('update_merchant_photo_orders', {
			photo_updates: updates
		})

		if (error) {
			// Fallback to individual updates if RPC doesn't exist
			if (error.code === 'PGRST202' || error.message.includes('function') || error.message.includes('does not exist')) {
				// RPC function doesn't exist, use individual updates
				const updatePromises = updates.map(({ id, order }) =>
					supabase
						.from('merchant_photos')
						.update({ order, updated_at: new Date().toISOString() })
						.eq('id', id)
				)

				const results = await Promise.all(updatePromises)
				const hasError = results.some(r => r.error)
				if (hasError) {
					console.error('Error in batch updates:', results.filter(r => r.error))
					return false
				}
				return true
			}

			console.error('Error updating photo orders:', error)
			return false
		}

		return true
	} catch (error) {
		console.error('updatePhotoOrders error:', error)
		return false
	}
}

/**
 * Fetch all photos for a merchant (logo + gallery)
 * @param {string} merchantId - UUID of the merchant
 * @returns {Promise<{logo: Object|null, gallery: Array}>} Logo and gallery photos sorted by order
 */
export async function fetchMerchantPhotos(merchantId) {
	if (!merchantId) return { logo: null, gallery: [] }

	const { data, error } = await supabase
		.from('merchant_photos')
		.select('*')
		.eq('merchant_id', merchantId)
		.order('order', { ascending: true })

	if (error) {
		console.error('Error fetching merchant photos:', error)
		return { logo: null, gallery: [] }
	}

	const logo = data?.find(photo => photo.is_avatar) || null
	const gallery = data?.filter(photo => !photo.is_avatar) || []

	return { logo, gallery }
}

/**
 * Upload merchant logo to storage and create/update database record
 * @param {string} merchantId - UUID of the merchant
 * @param {Blob} imageBlob - JPEG image blob to upload (500x500)
 * @param {string|null} existingPhotoId - Existing photo record ID for re-upload
 * @returns {Promise<{photo: Object, url: string}|null>} Photo record and URL or null on error
 */
export async function uploadMerchantLogo(merchantId, imageBlob, existingPhotoId = null) {
	try {
		let photoId = existingPhotoId
		let photoRecord = null

		if (existingPhotoId) {
			// Re-upload: delete old file and update record
			const { data: existingPhoto, error: fetchError } = await supabase
				.from('merchant_photos')
				.select('*')
				.eq('id', existingPhotoId)
				.single()

			if (fetchError) {
				console.error('Error fetching existing logo:', fetchError)
				throw fetchError
			}

			// Delete old file from storage
			if (existingPhoto.path) {
				const { error: deleteError } = await supabase.storage
					.from(existingPhoto.bucket)
					.remove([existingPhoto.path])

				if (deleteError) {
					console.error('Error deleting old logo file:', deleteError)
					// Continue anyway - file might not exist
				}
			}

			photoId = existingPhotoId
			photoRecord = existingPhoto
		} else {
			// First, check if there's an existing logo and delete it
			const existingLogo = await fetchMerchantLogo(merchantId)
			if (existingLogo) {
				await deleteMerchantPhoto(existingLogo.id, true) // Skip confirm for internal replacement
			}

			// Create new record in merchant_photos table
			const { data: newPhoto, error: insertError } = await supabase
				.from('merchant_photos')
				.insert({
					merchant_id: merchantId,
					bucket: BUCKET_NAME,
					is_avatar: true,
					path: '', // Will update after upload
					description: 'Merchant logo'
				})
				.select()
				.single()

			if (insertError) {
				console.error('Error creating logo record:', insertError)
				throw insertError
			}

			photoId = newPhoto.id
			photoRecord = newPhoto
		}

		// Upload to storage: {merchantId}/{photoId}.jpeg
		const filePath = `${merchantId}/${photoId}.jpeg`

		const { error: uploadError } = await supabase.storage
			.from(BUCKET_NAME)
			.upload(filePath, imageBlob, {
				contentType: 'image/jpeg',
				upsert: true
			})

		if (uploadError) {
			console.error('Error uploading logo to storage:', uploadError)
			// Clean up DB record if new
			if (!existingPhotoId) {
				await supabase.from('merchant_photos').delete().eq('id', photoId)
			}
			throw uploadError
		}

		// Update record with path and updated_at
		const { data: updatedPhoto, error: updateError } = await supabase
			.from('merchant_photos')
			.update({
				path: filePath,
				updated_at: new Date().toISOString()
			})
			.eq('id', photoId)
			.select()
			.single()

		if (updateError) {
			console.error('Error updating logo record:', updateError)
			throw updateError
		}

		const url = getMerchantPhotoUrl(updatedPhoto)

		return {
			photo: updatedPhoto,
			url
		}
	} catch (error) {
		console.error('uploadMerchantLogo error:', error)
		return null
	}
}

/**
 * Upload merchant gallery photo to storage and create database record
 * Auto-assigns order starting from 1
 * Preserves original file format (JPEG, PNG, WebP, etc.)
 * @param {string} merchantId - UUID of the merchant
 * @param {File|Blob} imageFile - Image file or blob to upload
 * @returns {Promise<{photo: Object, url: string}|null>} Photo record and URL or null on error
 */
export async function uploadMerchantGalleryPhoto(merchantId, imageFile) {
	try {
		// Get next order number
		const nextOrder = await getNextGalleryPhotoOrder(merchantId)

		// Determine file extension and content type from the file
		let fileExtension = 'jpeg'
		let contentType = 'image/jpeg'
		
		if (imageFile instanceof File) {
			// Get extension from filename or MIME type
			const fileName = imageFile.name || ''
			const mimeType = imageFile.type || 'image/jpeg'
			
			if (fileName.toLowerCase().endsWith('.png') || mimeType === 'image/png') {
				fileExtension = 'png'
				contentType = 'image/png'
			} else if (fileName.toLowerCase().endsWith('.webp') || mimeType === 'image/webp') {
				fileExtension = 'webp'
				contentType = 'image/webp'
			} else if (fileName.toLowerCase().endsWith('.gif') || mimeType === 'image/gif') {
				fileExtension = 'gif'
				contentType = 'image/gif'
			} else {
				// Default to jpeg for jpg, jpeg, or unknown
				fileExtension = 'jpeg'
				contentType = 'image/jpeg'
			}
		}

		// Create new record in merchant_photos table with auto-assigned order
		const { data: newPhoto, error: insertError } = await supabase
			.from('merchant_photos')
			.insert({
				merchant_id: merchantId,
				bucket: BUCKET_NAME,
				is_avatar: false,
				order: nextOrder,
				path: '', // Will update after upload
				description: 'Gallery photo'
			})
			.select()
			.single()

		if (insertError) {
			console.error('Error creating gallery photo record:', insertError)
			throw insertError
		}

		const photoId = newPhoto.id

		// Upload to storage: {merchantId}/{photoId}.{extension}
		const filePath = `${merchantId}/${photoId}.${fileExtension}`

		const { error: uploadError } = await supabase.storage
			.from(BUCKET_NAME)
			.upload(filePath, imageFile, {
				contentType: contentType,
				upsert: true
			})

		if (uploadError) {
			console.error('Error uploading gallery photo to storage:', uploadError)
			// Clean up DB record
			await supabase.from('merchant_photos').delete().eq('id', photoId)
			throw uploadError
		}

		// Update record with path and updated_at
		const { data: updatedPhoto, error: updateError } = await supabase
			.from('merchant_photos')
			.update({
				path: filePath,
				updated_at: new Date().toISOString()
			})
			.eq('id', photoId)
			.select()
			.single()

		if (updateError) {
			console.error('Error updating gallery photo record:', updateError)
			throw updateError
		}

		const url = getMerchantPhotoUrl(updatedPhoto)

		return {
			photo: updatedPhoto,
			url
		}
	} catch (error) {
		console.error('uploadMerchantGalleryPhoto error:', error)
		return null
	}
}

/**
 * Upload multiple merchant gallery photos in batch
 * Uses a single DB insert for all records, then parallel storage uploads
 * @param {string} merchantId - UUID of the merchant
 * @param {File[]} files - Array of image files to upload
 * @returns {Promise<{photos: Object[]}|null>} Array of photo records or null on error
 */
export async function uploadMerchantGalleryPhotoBatch(merchantId, files) {
	if (!files || files.length === 0) return null

	try {
		const nextOrder = await getNextGalleryPhotoOrder(merchantId)

		// Batch insert all DB records at once
		const insertRows = files.map((_, idx) => ({
			merchant_id: merchantId,
			bucket: BUCKET_NAME,
			is_avatar: false,
			order: nextOrder + idx,
			path: '',
			description: 'Gallery photo'
		}))

		const { data: newPhotos, error: insertError } = await supabase
			.from('merchant_photos')
			.insert(insertRows)
			.select()

		if (insertError) {
			console.error('Error batch creating gallery photo records:', insertError)
			throw insertError
		}

		// Upload all files to storage in parallel
		const uploadResults = await Promise.allSettled(
			newPhotos.map((photo, idx) => {
				const file = files[idx]
				const mimeType = file.type || 'image/jpeg'
				let ext = 'jpeg'
				if (mimeType === 'image/png') ext = 'png'
				else if (mimeType === 'image/webp') ext = 'webp'
				else if (mimeType === 'image/gif') ext = 'gif'

				const filePath = `${merchantId}/${photo.id}.${ext}`

				return supabase.storage
					.from(BUCKET_NAME)
					.upload(filePath, file, { contentType: mimeType, upsert: true })
					.then(({ error }) => {
						if (error) throw error
						return { photoId: photo.id, filePath }
					})
			})
		)

		// Batch update all paths in one call per photo (collect successful ones)
		const successfulUploads = []
		const failedIds = []

		uploadResults.forEach((result, idx) => {
			if (result.status === 'fulfilled') {
				successfulUploads.push(result.value)
			} else {
				failedIds.push(newPhotos[idx].id)
			}
		})

		// Clean up failed records
		if (failedIds.length > 0) {
			await supabase.from('merchant_photos').delete().in('id', failedIds)
		}

		// Update paths for successful uploads
		if (successfulUploads.length > 0) {
			await Promise.all(
				successfulUploads.map(({ photoId, filePath }) =>
					supabase.from('merchant_photos')
						.update({ path: filePath, updated_at: new Date().toISOString() })
						.eq('id', photoId)
				)
			)
		}

		// Fetch the final records
		const successIds = successfulUploads.map(u => u.photoId)
		if (successIds.length === 0) return null

		const { data: finalPhotos, error: fetchError } = await supabase
			.from('merchant_photos')
			.select('*')
			.in('id', successIds)
			.order('order', { ascending: true })

		if (fetchError) throw fetchError

		return { photos: finalPhotos || [] }
	} catch (error) {
		console.error('uploadMerchantGalleryPhotoBatch error:', error)
		return null
	}
}

/**
 * Delete merchant photo from storage and database
 * @param {string} photoId - UUID of the merchant_photos record
 * @param {boolean} skipConfirm - Skip confirmation dialog (default: false)
 * @returns {Promise<boolean|null>} true = success, false = error, null = cancelled
 */
export async function deleteMerchantPhoto(photoId, skipConfirm = false) {
	try {
		// Confirm with user before deleting
		if (!skipConfirm) {
			const confirmed = window.confirm('Are you sure you want to delete this photo?')
			if (!confirmed) {
				return null // User cancelled - not an error
			}
		}

		// Fetch the photo record
		const { data: photo, error: fetchError } = await supabase
			.from('merchant_photos')
			.select('*')
			.eq('id', photoId)
			.single()

		if (fetchError) {
			console.error('Error fetching photo for deletion:', fetchError)
			return false
		}

		// Delete from storage
		if (photo.path) {
			const { error: deleteStorageError } = await supabase.storage
				.from(photo.bucket)
				.remove([photo.path])

			if (deleteStorageError) {
				console.error('Error deleting from storage:', deleteStorageError)
				// Continue to delete DB record anyway
			}
		}

		// Delete from database
		const { error: deleteDbError } = await supabase
			.from('merchant_photos')
			.delete()
			.eq('id', photoId)

		if (deleteDbError) {
			console.error('Error deleting photo record:', deleteDbError)
			return false
		}

		return true
	} catch (error) {
		console.error('deleteMerchantPhoto error:', error)
		return false
	}
}

/**
 * Delete all photos for a merchant from storage and database
 * @param {string} merchantId - UUID of the merchant
 * @returns {Promise<boolean>} Success status
 */
export async function deleteAllMerchantPhotos(merchantId) {
	if (!merchantId) return true

	try {
		// Fetch all photos for this merchant
		const { data: photos, error: fetchError } = await supabase
			.from('merchant_photos')
			.select('*')
			.eq('merchant_id', merchantId)

		if (fetchError) {
			console.error('Error fetching merchant photos for deletion:', fetchError)
			return false
		}

		if (!photos || photos.length === 0) {
			return true // No photos to delete
		}

		// Collect all storage paths to delete
		const pathsToDelete = photos
			.filter(photo => photo.path)
			.map(photo => photo.path)

		// Delete from storage (batch delete)
		if (pathsToDelete.length > 0) {
			const { error: deleteStorageError } = await supabase.storage
				.from(BUCKET_NAME)
				.remove(pathsToDelete)

			if (deleteStorageError) {
				console.error('Error deleting merchant photos from storage:', deleteStorageError)
				// Continue to delete DB records anyway
			}
		}

		// Delete all records from database
		const { error: deleteDbError } = await supabase
			.from('merchant_photos')
			.delete()
			.eq('merchant_id', merchantId)

		if (deleteDbError) {
			console.error('Error deleting merchant photo records:', deleteDbError)
			return false
		}

		return true
	} catch (error) {
		console.error('deleteAllMerchantPhotos error:', error)
		return false
	}
}
